
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

const loanUseOptions = ['Refinance', 'Home Purchase'];
const propertyOptionsStep = ['Single Family', 'Multi Family', 'Town House', 'Condominium'];
const creditGrades = ['Excellent (680+)', 'Good (640 - 679)', 'Average (600 - 639)', 'Fair (560 - 599)', 'Poor (<=559)'];
const yesNo = ['Yes', 'No'];
const mortgageLateOptions = ['None', 'One', 'Two or More'];
const buyTimelineOptions = [
  'Offer submitted or under contract',
  "Soon - I'm actively touring homes",
  'Within 1-2 months',
  'Within 3-6 months',
  'Just exploring',
];
const propertyPurposeOptions = ['Primary Residence', 'Vacation Property', 'Investment Property'];
const downPaymentPercents = [0, 3.5, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 60, 70, 80, 90, 100];
const cashUseOptions = [
  'Tackle credit cards',
  'Pay down taxes or other debt',
  'Update my home',
  'Fund education',
  'Business needs',
  'Medical expenses',
  'Grow savings',
  'Other uses',
  'No cash needed',
];
const sliderValues = (() => {
  const values = [];
  for (let v = 50000; v <= 195000; v += 5000) values.push(v);
  for (let v = 205000; v <= 400000; v += 10000) values.push(v);
  for (let v = 420000; v <= 1000000; v += 20000) values.push(v);
  for (let v = 1250000; v <= 2000000; v += 250000) values.push(v);
  return values;
})();

const balanceGuides = [
  { home: 55000, balance: 55000 },
  { home: 70000, balance: 60000 },
  { home: 80000, balance: 65000 },
  { home: 85000, balance: 70000 },
  { home: 90000, balance: 75000 },
  { home: 95000, balance: 80000 },
  { home: 105000, balance: 85000 },
  { home: 110000, balance: 90000 },
  { home: 115000, balance: 95000 },
  { home: 120000, balance: 100000 },
  { home: 130000, balance: 105000 },
  { home: 135000, balance: 110000 },
  { home: 140000, balance: 115000 },
  { home: 145000, balance: 120000 },
  { home: 155000, balance: 125000 },
  { home: 160000, balance: 130000 },
  { home: 165000, balance: 135000 },
  { home: 170000, balance: 140000 },
  { home: 180000, balance: 145000 },
  { home: 185000, balance: 150000 },
  { home: 190000, balance: 155000 },
  { home: 195000, balance: 160000 },
  { home: 210000, balance: 170000 },
  { home: 220000, balance: 180000 },
  { home: 230000, balance: 185000 },
  { home: 240000, balance: 195000 },
  { home: 250000, balance: 200000 },
  { home: 270000, balance: 220000 },
  { home: 280000, balance: 230000 },
  { home: 290000, balance: 240000 },
  { home: 310000, balance: 250000 },
  { home: 320000, balance: 260000 },
  { home: 330000, balance: 270000 },
  { home: 340000, balance: 280000 },
  { home: 360000, balance: 290000 },
  { home: 370000, balance: 300000 },
  { home: 380000, balance: 310000 },
  { home: 390000, balance: 320000 },
  { home: 420000, balance: 340000 },
  { home: 440000, balance: 360000 },
  { home: 460000, balance: 370000 },
  { home: 480000, balance: 390000 },
  { home: 500000, balance: 400000 },
  { home: 520000, balance: 420000 },
  { home: 540000, balance: 440000 },
  { home: 560000, balance: 460000 },
  { home: 580000, balance: 480000 },
  { home: 600000, balance: 480000 },
  { home: 620000, balance: 500000 },
  { home: 640000, balance: 520000 },
  { home: 660000, balance: 540000 },
  { home: 680000, balance: 560000 },
  { home: 720000, balance: 580000 },
  { home: 740000, balance: 600000 },
  { home: 760000, balance: 620000 },
  { home: 780000, balance: 640000 },
  { home: 820000, balance: 660000 },
  { home: 840000, balance: 680000 },
  { home: 860000, balance: 700000 },
  { home: 880000, balance: 720000 },
  { home: 920000, balance: 740000 },
  { home: 940000, balance: 760000 },
  { home: 960000, balance: 780000 },
  { home: 980000, balance: 800000 },
  { home: 1100000, balance: 880000 },
  { home: 1200000, balance: 960000 },
  { home: 1300000, balance: 1100000 },
  { home: 1400000, balance: 1200000 },
  { home: 1600000, balance: 1300000 },
  { home: 1700000, balance: 1400000 },
  { home: 1800000, balance: 1500000 },
  { home: 1900000, balance: 1600000 },
  { home: 2000000, balance: 1700000 },
];

const balanceThresholds = Array.from(new Map(balanceGuides.map(({ home, balance }) => [home, balance])).entries())
  .sort((a, b) => a[0] - b[0])
  .map(([home, balance]) => ({ home, balance }));

const rateSteps = (() => {
  const arr = ['3% or lower'];
  let v = 3.25;
  while (v <= 10) {
    arr.push(v);
    v = Math.round((v + 0.25) * 100) / 100;
  }
  arr.push('over 10%');
  return arr;
})();

const formatCurrency = (value) => `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const formatCurrencyWithPlus = (value, max) =>
  value >= max ? `$${max.toLocaleString(undefined, { maximumFractionDigits: 0 })}+` : formatCurrency(value);
const formatRateLabel = (index) => {
  if (index <= 0) return '3% or lower';
  if (index >= rateSteps.length - 1) return 'Over 10%';
  const val = rateSteps[index];
  return `${Number(val).toFixed(2).replace(/\.00$/, '')}%`;
};

const getAutoBalance = (homeValue) => {
  let target = balanceThresholds[0].balance;
  for (const { home, balance } of balanceThresholds) {
    if (homeValue >= home) target = balance;
    else break;
  }
  return target;
};

const findClosestBalanceIndex = (homeIdx, targetBalance) => {
  let bestBalanceIndex = 0;
  let bestDiff = Number.MAX_SAFE_INTEGER;
  for (let i = 0; i <= homeIdx; i += 1) {
    const diff = Math.abs(sliderValues[i] - targetBalance);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestBalanceIndex = i;
    }
  }
  return bestBalanceIndex;
};

const defaultHomeValue = 350000;
const defaultHomeIndex = Math.max(0, sliderValues.indexOf(defaultHomeValue));
const defaultBalanceValue = getAutoBalance(defaultHomeValue);
const defaultBalanceIndex = findClosestBalanceIndex(defaultHomeIndex, defaultBalanceValue);
const defaultRateIndex = Math.max(0, rateSteps.findIndex((step) => step === 5.75));
const defaultRateLabel = formatRateLabel(defaultRateIndex);

const RefiIcon = ({ size = 22 }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: size, height: size, stroke: '#ffffff', strokeWidth: 1.7, fill: 'none' }}
    aria-hidden
  >
    <path d="M4 11.5 12 5l8 6.5" />
    <path d="M6 10.5v7h4v-4h4v4h4v-7" />
  </Box>
);

const PurchaseIcon = ({ size = 22 }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: size, height: size, stroke: '#ffffff', strokeWidth: 1.7, fill: 'none' }}
    aria-hidden
  >
    <rect x="4" y="9" width="16" height="10" rx="2" />
    <path d="M8 9V7a4 4 0 0 1 8 0v2" />
    <path d="M9 14h6" />
  </Box>
);

const badge = (text, color) => (
  <Chip
    label={text}
    size="small"
    sx={
      {
        bgcolor: color,
        color: '#0b1b32',
        fontWeight: 700,
        letterSpacing: 0.2,
      }
    }
  />
);
const Form = () => {
  const [formData, setFormData] = useState({
    PRODUCT: loanUseOptions[0],
    PROP_ZIP: '',
    PROP_DESC: propertyOptionsStep[0],
    CRED_GRADE: '',
    EST_VAL: sliderValues[defaultHomeIndex],
    BAL_ONE: sliderValues[defaultBalanceIndex],
    ADD_CASH: 0,
    LOAN_TYPE: '',
    FHA_BANK_FORECLOSURE: '',
    ANNUAL_VERIFIABLE_INCOME: '',
    NUM_MORTGAGE_LATES: '',
    VA_STATUS: '',
    VA_BRANCH: '',
    EMAIL: '',
    FNAME: '',
    LNAME: '',
    ADDRESS: '',
    CITY: '',
    STATE: '',
    ZIP: '',
    PRI_PHON: '',
    INTEREST_RATE: defaultRateLabel,
  });

  const [step, setStep] = useState(1);
  const [homeIndex, setHomeIndex] = useState(defaultHomeIndex);
  const [balanceIndex, setBalanceIndex] = useState(defaultBalanceIndex);
  const [purchaseValueIndex, setPurchaseValueIndex] = useState(0);
  const [downPaymentIndex, setDownPaymentIndex] = useState(0);
  const [cashUses, setCashUses] = useState([]);
  const [equityValue, setEquityValue] = useState(0);
  const [zipInfo, setZipInfo] = useState('');
  const [zipLookupError, setZipLookupError] = useState('');
  const [zipError, setZipError] = useState('');
  const [rateIndex, setRateIndex] = useState(defaultRateIndex);
  const [loanChoice, setLoanChoice] = useState('');
  const [creditChoice, setCreditChoice] = useState('');
  const [bankChoice, setBankChoice] = useState('');
  const [incomeChoice, setIncomeChoice] = useState('');
  const [lateChoice, setLateChoice] = useState('');
  const [veteranChoice, setVeteranChoice] = useState('');
  const [branchChoice, setBranchChoice] = useState('');
  const [propertyPurpose, setPropertyPurpose] = useState('');
  const [buyTimeline, setBuyTimeline] = useState('');
  const [goalChoice, setGoalChoice] = useState('');
  const [isPurchase, setIsPurchase] = useState(false);
  const [result, setResult] = useState(null);

  const maxIndex = sliderValues.length - 1;
  const topValue = useMemo(() => sliderValues[sliderValues.length - 1], []);
  const totalSteps = 15;
  const progressPercent = Math.round((step / totalSteps) * 100);
  const noCashNeeded = cashUses.includes('No cash needed') || cashUses.includes('No Cash Needed');

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleHomeChange = (_e, value) => {
    const nextIndex = Math.max(0, Math.min(value, maxIndex));
    setHomeIndex(nextIndex);
    const nextVal = sliderValues[nextIndex];
    handleChange('EST_VAL', nextVal);

    const targetBalance = getAutoBalance(nextVal);
    const bestBalanceIndex = findClosestBalanceIndex(nextIndex, targetBalance);
    setBalanceIndex(bestBalanceIndex);
    handleChange('BAL_ONE', sliderValues[bestBalanceIndex]);
  };

  const handleBalanceChange = (_e, value) => {
    const capped = Math.max(0, Math.min(value, homeIndex));
    setBalanceIndex(capped);
    handleChange('BAL_ONE', sliderValues[capped]);
  };

  const handleZipInput = async (val, targetField = 'ZIP') => {
    if (!/^\d{0,5}$/.test(val)) return;
    handleChange(targetField, val);
    handleChange('PROP_ZIP', val);
    setZipError('');
    if (val.length === 5) {
      await lookupZip(val);
    } else {
      setZipInfo('');
      setZipLookupError('');
    }
  };

  const lookupZip = async (zip) => {
    try {
      setZipInfo('');
      setZipLookupError('');
      const resp = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!resp.ok) {
        setZipLookupError('Could not fetch city/state for this ZIP.');
        return;
      }
      const data = await resp.json();
      const place = data.places && data.places[0];
      if (place) {
        setZipInfo(`${place['place name']}, ${place['state abbreviation']}`);
      }
    } catch (_e) {
      setZipLookupError('Could not fetch city/state for this ZIP.');
    }
  };

  const toggleCashUse = (value) => {
    setCashUses((prev) => (prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setResult({ ok: true, message: 'UI-only preview. Lead posting disabled for now.' });
  };

  const eqMax = Math.max(formData.EST_VAL - formData.BAL_ONE, 0);
  const eqDefault = Math.max(Math.floor(formData.EST_VAL * 0.85 - formData.BAL_ONE), 0);
  const cappedDefault = Math.min(eqDefault, eqMax);
  const shell = (title, subtitle, body) => (
    <Stack spacing={2} sx={{ width: '100%' }}>
      <Box>
        <Typography variant="h5" fontWeight={800} color="text.primary">
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {body}
    </Stack>
  );

const primaryBtnSx = { fontWeight: 800, py: { xs: 0.9, md: 1.05 }, px: { xs: 2.5, md: 3 }, borderRadius: 999, boxShadow: '0 12px 34px rgba(247,127,0,0.24)' };
const outlineBtnSx = { fontWeight: 700, py: { xs: 0.85, md: 1.05 }, px: { xs: 2.5, md: 3 }, borderRadius: 999 };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Stack spacing={{ xs: 2.5, md: 3 }} alignItems="center" textAlign="center" sx={{ width: '100%' }}>
            <Typography variant="h6" fontWeight={800} color="text.secondary" sx={{ fontSize: { xs: '1.6rem', md: '2.4rem' } }}>
              What are you looking to do?
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              justifyContent="center"
              alignItems="stretch"
              sx={{ width: '100%', maxWidth: 720 }}
            >
              {[
                { label: 'Refinance', value: 'Refinance', icon: <RefiIcon /> },
                { label: 'Need a Home Purchase Loan', value: 'Home Purchase', icon: <PurchaseIcon /> },
              ].map((opt) => (
                <Button
                  key={opt.value}
                  fullWidth
                  variant="contained"
                  color="info"
                  onClick={() => {
                    const purchase = opt.value === 'Home Purchase';
                    setGoalChoice(opt.value);
                    setIsPurchase(purchase);
                    handleChange('PRODUCT', opt.value);
                    setStep(2);
                  }}
                  sx={{
                    ...primaryBtnSx,
                    boxShadow: 'none',
                    minHeight: { xs: 56, md: 64 },
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {opt.icon}
                  {opt.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        );
      case 2:
        return shell(
          'Start with your property ZIP',
          "We'll use this to tailor guidelines for your area.",
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Property ZIP code"
              name="PROP_ZIP"
              value={formData.PROP_ZIP}
              onChange={(e) => handleZipInput(e.target.value)}
              error={!!zipError}
              helperText={zipError || 'Enter 5 digits'}
              inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '\\d{5}' }}
              sx={{ maxWidth: 320 }}
            />
            {zipInfo ? (
              <Typography variant="body2" color="text.secondary">
                {zipInfo}
              </Typography>
            ) : null}
            {zipLookupError ? (
              <Typography variant="body2" color="error">
                {zipLookupError}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button
                variant="contained"
                color="info"
                disableElevation
                onClick={() => {
                  if (!/^\d{5}$/.test(formData.PROP_ZIP)) {
                    setZipError('Valid ZIP is required to move on.');
                    return;
                  }
                  setZipError('');
                  setStep(3);
                }}
                sx={{ ...primaryBtnSx, boxShadow: 'none' }}
              >
                Save ZIP & continue
              </Button>
              <Button variant="text" color="inherit" onClick={() => setStep(1)} sx={{ fontWeight: 700 }}>
                Back
              </Button>
            </Stack>
          </Stack>,
        );
      case 3:
        return shell(
          'Tell us about the property',
          'Choose the closest match.',
          <Stack spacing={1.2}>
            {propertyOptionsStep.map((opt) => (
              <Button
                key={opt}
                fullWidth
                variant={formData.PROP_DESC === opt ? 'contained' : 'outlined'}
                color="info"
                onClick={() => {
                  handleChange('PROP_DESC', opt);
                  setStep(4);
                }}
                sx={{ ...primaryBtnSx, boxShadow: 'none', justifyContent: 'space-between' }}
              >
                <Typography fontWeight={800}>{opt}</Typography>
                {formData.PROP_DESC === opt ? badge('Selected', '#f77f00') : null}
              </Button>
            ))}
            <Button variant="text" color="inherit" onClick={() => setStep(2)} sx={{ mt: 0.5 }}>
              Back
            </Button>
          </Stack>,
        );
      case 4:
        return shell(
          isPurchase ? 'What price range are you shopping in?' : 'What is your home worth today?',
          isPurchase ? 'A ballpark estimate is perfect.' : 'A recent appraisal or online estimate is fine.',
          <Stack spacing={1.5}>
            <Slider
              value={isPurchase ? purchaseValueIndex : homeIndex}
              onChange={isPurchase ? (_e, v) => setPurchaseValueIndex(Array.isArray(v) ? v[0] : v) : handleHomeChange}
              min={0}
              max={maxIndex}
              step={1}
              sx={{ '& .MuiSlider-thumb': { width: 26, height: 26 } }}
            />
            <Typography variant="h6" fontWeight={800}>
              {formatCurrencyWithPlus(sliderValues[isPurchase ? purchaseValueIndex : homeIndex], topValue)}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" color="info" sx={primaryBtnSx} onClick={() => setStep(5)}>
                Continue
              </Button>
              <Button variant="text" color="inherit" onClick={() => setStep(3)}>
                Back
              </Button>
            </Stack>
          </Stack>,
        );
      case 5:
        return shell(
          isPurchase ? 'How much are you putting down?' : 'What is your current mortgage balance?',
          isPurchase ? 'Move the slider to match your planned down payment.' : 'Include any second mortgages or HELOCs.',
          <Stack spacing={1.5}>
            <Slider
              value={isPurchase ? downPaymentIndex : balanceIndex}
              onChange={
                isPurchase
                  ? (_e, v) => setDownPaymentIndex(Array.isArray(v) ? v[0] : v)
                  : handleBalanceChange
              }
              min={0}
              max={isPurchase ? downPaymentPercents.length - 1 : homeIndex}
              step={1}
              sx={{ '& .MuiSlider-thumb': { width: 26, height: 26 } }}
            />
            <Typography variant="h6" fontWeight={800}>
              {isPurchase
                ? `${downPaymentPercents[downPaymentIndex]}% (${formatCurrency(
                    Math.round((downPaymentPercents[downPaymentIndex] / 100) * sliderValues[purchaseValueIndex]),
                  )})`
                : formatCurrencyWithPlus(sliderValues[balanceIndex], topValue)}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" color="info" sx={primaryBtnSx} onClick={() => setStep(6)}>
                Continue
              </Button>
              <Button variant="text" color="inherit" onClick={() => setStep(4)}>
                Back
              </Button>
            </Stack>
          </Stack>,
        );
      case 6:
        return shell(
          isPurchase ? 'What will this place be for?' : 'How would you use extra cash?',
          isPurchase ? 'Choose your intended use.' : "Pick one or more - we'll estimate a sensible amount.",
          isPurchase ? (
            <Stack spacing={1.2}>
              {propertyPurposeOptions.map((opt) => (
                <Button
                  key={opt}
                  fullWidth
                  variant={propertyPurpose === opt ? 'contained' : 'outlined'}
                  color="info"
                  onClick={() => {
                    setPropertyPurpose(opt);
                    setStep(7);
                  }}
                  sx={{ ...primaryBtnSx, boxShadow: 'none' }}
                >
                  {opt}
                </Button>
              ))}
              <Button variant="text" color="inherit" onClick={() => setStep(5)}>
                Back
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <FormGroup sx={{ gap: 0.5 }}>
                {cashUseOptions.map((option) => (
                  <FormControlLabel
                    key={option}
                    control={<Checkbox checked={cashUses.includes(option)} onChange={() => toggleCashUse(option)} />}
                    label={option}
                  />
                ))}
              </FormGroup>
              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  color="info"
                  sx={primaryBtnSx}
                  onClick={() => {
                    const initEq = equityValue ?? cappedDefault;
                    setEquityValue(initEq);
                    setStep(7);
                  }}
                >
                  Continue
                </Button>
                <Button variant="text" color="inherit" onClick={() => setStep(5)}>
                  Back
                </Button>
              </Stack>
            </Stack>
          ),
        );
      case 7:
        return shell(
          isPurchase ? 'When are you planning to buy?' : 'How much cash-out makes sense?',
          isPurchase ? 'Your timeline helps us match programs.' : 'Slide to a comfortable amount (we cap at estimated equity).',
          isPurchase ? (
            <Stack spacing={1.2}>
              {buyTimelineOptions.map((opt) => (
                <Button
                  key={opt}
                  fullWidth
                  variant={buyTimeline === opt ? 'contained' : 'outlined'}
                  color="info"
                  onClick={() => {
                    setBuyTimeline(opt);
                    setStep(8);
                  }}
                  sx={{ ...primaryBtnSx, boxShadow: 'none', textTransform: 'none' }}
                >
                  {opt}
                </Button>
              ))}
              <Button variant="text" color="inherit" onClick={() => setStep(6)}>
                Back
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Slider
                value={Math.min(equityValue ?? cappedDefault, eqMax)}
                min={0}
                max={eqMax}
                step={1000}
                onChange={(_e, val) => setEquityValue(val)}
                disabled={eqMax <= 0}
                sx={{ '& .MuiSlider-thumb': { width: 26, height: 26 } }}
              />
              <Typography variant="h6" fontWeight={800}>
                {formatCurrency(Math.min(equityValue || cappedDefault, eqMax))}
              </Typography>
              <Box sx={{ bgcolor: 'rgba(11,57,84,0.06)', p: 2, borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  Based on your numbers we estimated {formatCurrency(Math.max(cappedDefault, 0))} using an 85% loan-to-value. Programs vary by
                  lender and credit profile.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1.5}>
                <Button variant="contained" color="info" sx={primaryBtnSx} onClick={() => setStep(8)}>
                  Continue
                </Button>
                <Button variant="text" color="inherit" onClick={() => setStep(6)}>
                  Back
                </Button>
              </Stack>
            </Stack>
          ),
        );
      case 8:
        return shell(
          'Where is your current rate sitting?',
          'A ballpark number is fine.',
          <Stack spacing={1.5}>
            <Slider
              value={rateIndex}
              onChange={(_e, val) => {
                const idx = Array.isArray(val) ? val[0] : val;
                setRateIndex(idx);
                handleChange('INTEREST_RATE', formatRateLabel(idx));
              }}
              min={0}
              max={rateSteps.length - 1}
              step={1}
              sx={{ '& .MuiSlider-thumb': { width: 26, height: 26 } }}
            />
            <Typography variant="h6" fontWeight={800}>
              {formatRateLabel(rateIndex)}
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" color="info" sx={primaryBtnSx} onClick={() => setStep(9)}>
                Continue
              </Button>
              <Button variant="text" color="inherit" onClick={() => setStep(7)}>
                Back
              </Button>
            </Stack>
          </Stack>,
        );
      case 9:
        return shell(
          "How's your credit profile?",
          'Select the closest range.',
          <Stack spacing={1.2}>
            {creditGrades.map((opt) => (
              <Button
                key={opt}
                fullWidth
                variant={creditChoice === opt ? 'contained' : 'outlined'}
                color="info"
                onClick={() => {
                  setCreditChoice(opt);
                  handleChange('CRED_GRADE', opt);
                  setStep(10);
                }}
                sx={{ ...primaryBtnSx, boxShadow: 'none', textTransform: 'none' }}
              >
                {opt}
              </Button>
            ))}
            <Button variant="text" color="inherit" onClick={() => setStep(8)}>
              Back
            </Button>
          </Stack>,
        );
      case 10:
        return shell(
          'Any bankruptcies or foreclosures in the last 3 years?',
          '',
          <Stack spacing={1.2}>
            {yesNo.map((opt) => (
              <Button
                key={opt}
                fullWidth
                variant={bankChoice === opt ? 'contained' : 'outlined'}
                color="info"
                onClick={() => {
                  setBankChoice(opt);
                  handleChange('FHA_BANK_FORECLOSURE', opt);
                  setStep(11);
                }}
                sx={{ ...primaryBtnSx, boxShadow: 'none' }}
              >
                {opt}
              </Button>
            ))}
            <Button variant="text" color="inherit" onClick={() => setStep(9)}>
              Back
            </Button>
          </Stack>,
        );
      case 11:
        return shell(
          'Can you verify your income?',
          'Pay stubs, W2s, or bank statements count.',
          <Stack spacing={1.2}>
            {yesNo.map((opt) => (
              <Button
                key={opt}
                fullWidth
                variant={incomeChoice === opt ? 'contained' : 'outlined'}
                color="info"
                onClick={() => {
                  setIncomeChoice(opt);
                  handleChange('ANNUAL_VERIFIABLE_INCOME', opt);
                  setStep(12);
                }}
                sx={{ ...primaryBtnSx, boxShadow: 'none' }}
              >
                {opt}
              </Button>
            ))}
            <Button variant="text" color="inherit" onClick={() => setStep(10)}>
              Back
            </Button>
          </Stack>,
        );
      case 12:
        return shell(
          'Any late mortgage payments in the past year?',
          '',
          <Stack spacing={1.2}>
            {mortgageLateOptions.map((opt) => (
              <Button
                key={opt}
                fullWidth
                variant={lateChoice === opt ? 'contained' : 'outlined'}
                color="info"
                onClick={() => {
                  setLateChoice(opt);
                  handleChange('NUM_MORTGAGE_LATES', opt);
                  setStep(13);
                }}
                sx={{ ...primaryBtnSx, boxShadow: 'none' }}
              >
                {opt}
              </Button>
            ))}
            <Button variant="text" color="inherit" onClick={() => setStep(11)}>
              Back
            </Button>
          </Stack>,
        );
      case 13:
        return shell(
          'Have you or a spouse served in the U.S. military?',
          '',
          <Stack spacing={2}>
            <Stack spacing={1.2}>
              {yesNo.map((opt) => (
                <Button
                  key={opt}
                  fullWidth
                  variant={veteranChoice === opt ? 'contained' : 'outlined'}
                  color="info"
                  onClick={() => {
                    setVeteranChoice(opt);
                    handleChange('VA_STATUS', opt);
                    if (opt === 'No') {
                      setBranchChoice('');
                      handleChange('VA_BRANCH', '');
                      setStep(14);
                    }
                  }}
                  sx={{ ...primaryBtnSx, boxShadow: 'none' }}
                >
                  {opt}
                </Button>
              ))}
            </Stack>
            {veteranChoice === 'Yes' ? (
              <TextField
                select
                fullWidth
                label="Which branch?"
                value={branchChoice}
                onChange={(e) => {
                  setBranchChoice(e.target.value);
                  handleChange('VA_BRANCH', e.target.value);
                  if (e.target.value) setStep(14);
                }}
              >
                <MenuItem value="">Select a branch</MenuItem>
                <MenuItem value="Air Force">Air Force</MenuItem>
                <MenuItem value="Army">Army</MenuItem>
                <MenuItem value="Coast Guard">Coast Guard</MenuItem>
                <MenuItem value="Marines">Marines</MenuItem>
                <MenuItem value="Navy">Navy</MenuItem>
                <MenuItem value="National Guard">National Guard</MenuItem>
              </TextField>
            ) : null}
            <Button variant="text" color="inherit" onClick={() => setStep(12)}>
              Back
            </Button>
          </Stack>,
        );
      case 14:
        return shell(
          isPurchase ? 'Where can we send details?' : 'Confirm your property address',
          'This helps lenders price your scenario accurately.',
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Street address" name="ADDRESS" value={formData.ADDRESS} onChange={(e) => handleChange('ADDRESS', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ZIP code"
                  name="ZIP"
                  value={formData.ZIP}
                  onChange={(e) => handleZipInput(e.target.value)}
                  inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '\\d{5}' }}
                  error={!!zipError}
                  helperText={zipError || ' '}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="City" name="CITY" value={formData.CITY} onChange={(e) => handleChange('CITY', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="State" name="STATE" value={formData.STATE} onChange={(e) => handleChange('STATE', e.target.value)} />
              </Grid>
            </Grid>
            {zipInfo ? (
              <Typography variant="body2" color="text.secondary">
                {zipInfo}
              </Typography>
            ) : null}
            {zipLookupError ? (
              <Typography variant="body2" color="error">
                {zipLookupError}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                color="info"
                sx={primaryBtnSx}
                onClick={() => {
                  if (!formData.ADDRESS || !/^\d{5}$/.test(formData.ZIP || formData.PROP_ZIP)) {
                    setZipError('ZIP is required.');
                    return;
                  }
                  setStep(15);
                }}
              >
                Continue
              </Button>
              <Button variant="text" color="inherit" onClick={() => setStep(13)}>
                Back
              </Button>
            </Stack>
          </Stack>,
        );
      case 15:
        return shell(
          'Almost there - how can we reach you?',
          "We'll pair you with up to a few fitting lenders.",
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="First name" name="FNAME" value={formData.FNAME} onChange={(e) => handleChange('FNAME', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Last name" name="LNAME" value={formData.LNAME} onChange={(e) => handleChange('LNAME', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Email" name="EMAIL" type="email" value={formData.EMAIL} onChange={(e) => handleChange('EMAIL', e.target.value)} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Phone number" name="PRI_PHON" type="tel" value={formData.PRI_PHON} onChange={(e) => handleChange('PRI_PHON', e.target.value)} />
              </Grid>
            </Grid>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Button type="submit" variant="contained" color="info" sx={primaryBtnSx}>
                See my matches
              </Button>
              <Button variant="text" color="inherit" onClick={() => setStep(14)}>
                Back
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              By clicking "See my matches," you agree to our Terms &amp; Privacy Policy and consent to be contacted (call, text, or email) by lenders
              in our network. Consent is not required to get a quote. Message and data rates may apply.
            </Typography>
          </Stack>,
        );
      default:
        return null;
    }
  };
  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 1.5, md: 3 }, py: { xs: 1.5, md: 2.5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.25, md: 4 },
          borderRadius: { xs: 2, md: 4 },
          bgcolor: '#fff',
          boxShadow: 'none',
          border: '1px solid rgba(0,0,0,0.06)',
          width: '100%',
          maxWidth: 1100,
          mx: 'auto',
        }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box>
              {step > 1 ? (isPurchase ? badge('Buying', '#c9e2ff') : badge('Cash-out', '#c9e2ff')) : null}
              <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ mt: 1 }}>
                Average homeowners could cash out around $185,000*
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                Take this quick survey to see which mortgage options you could qualify for.
              </Typography>
            </Box>
            {step > 1 ? (
              <Stack alignItems="flex-end" spacing={1} sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary">{`Step ${step} of ${totalSteps}`}</Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{ width: '100%', height: 8, borderRadius: 999, backgroundColor: 'rgba(11,57,84,0.08)' }}
                />
              </Stack>
            ) : null}
          </Box>

          <Divider />

          <Box sx={{ py: 1 }}>{renderStep()}</Box>

          {result ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: result.ok ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                borderColor: result.ok ? 'success.main' : 'error.main',
                borderRadius: 3,
              }}
            >
              <Typography fontWeight={700} mb={1}>
                Server response
              </Typography>
              <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 13, m: 0 }}>
                {JSON.stringify(result, null, 2)}
              </Typography>
            </Paper>
          ) : null}
        </Stack>
      </Paper>
    </Container>
  );
};

export default Form;
